/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import rfdc from 'rfdc';
import { ChatMessageKind, MessagePartKind, } from '../model.js';
import { unreacheable } from './unreacheable.js';
// initialize clone function.
const clone = rfdc({
    circles: false,
    proto: false,
});
/**
 * Compare two values.
 *
 * @returns true if equals, false otherwise.
 */
function compareValues(obj1, obj2) {
    if (typeof obj1 === 'object' && typeof obj2 === 'object') {
        // case 1: two objects.
        // get keys of both objects.
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);
        // check if they have the same number of keys.
        if (keys1.length !== keys2.length) {
            return false;
        }
        // check if values of each key are the same.
        for (const key of keys1) {
            if (!compareValues(obj1[key], obj2[key])) {
                return false;
            }
        }
        return true;
    }
    // case 2: two values.
    return obj1 === obj2;
}
/**
 * Create a new discussion.
 *
 * @returns a new discussion object.
 */
function createNewDiscussion(flowName, context, useContextAsId, enableHitl) {
    return {
        settings: {
            flowName,
            context,
            useContextAsId,
            enableHitl,
        },
        chatSessions: [
            {
                isOpen: true,
                messages: [],
            },
        ],
        isWaitingForResponse: false,
        isWaitingForHitlUserAnswer: false,
        welcomeMessage: '',
        suggestedPrompts: [],
    };
}
/**
 * Check if two discussions have the same id.
 *
 * @param discussion1 the first discussion.
 * @param discussion2 the second discussion.
 */
export function areDiscussionSettingsEqual(settings1, settings2) {
    // check for the same flow name.
    if (settings1.flowName !== settings2.flowName) {
        return false;
    }
    // check for the same options.
    if (settings1.useContextAsId !== settings2.useContextAsId) {
        return false;
    }
    // compare context.
    if (settings1.useContextAsId) {
        return compareValues(settings1.context, settings2.context);
    }
    // all is identical.
    return true;
}
/**
 * Get the count of messages in the given discussion, for the last session only.
 */
export function getLastSessionMessageCount(discussion) {
    // guard.
    if (discussion === undefined) {
        return 0;
    }
    // guard.
    if (discussion.chatSessions.length === 0) {
        return 0;
    }
    // get the number of messages in the last session.
    return discussion.chatSessions[discussion.chatSessions.length - 1].messages
        .length;
}
/**
 * Find a discussion.
 *
 * @param discussions the set of discussions.
 * @param flowName the flowname.
 * @param context the context.
 * @returns a discussion or undefined if not found.
 */
export function findDiscussion(discussions, flowName, context, useContextAsId) {
    for (const discussion of discussions) {
        // get settings.
        const { settings } = discussion;
        // iterate over discussions.
        if (settings.flowName === flowName &&
            settings.useContextAsId === useContextAsId) {
            // the discussion share the same properties.
            if (useContextAsId) {
                // same props, and context comparison needed.
                if (compareValues(settings.context, context)) {
                    // in this discussion, the context matter.
                    return discussion;
                }
            }
            else {
                // same props, compare flow name only.
                return discussion;
            }
        }
    }
    // not found.
    return undefined;
}
/**
 * Get a discussion, or create it.
 *
 * @param discussions the set of discussions.
 * @param flowName the flowname.
 * @param context the context.
 * @param useContextAsId the use context as id flag.
 * @param enableHitl the enable HITL flag.
 * @returns a discussion.
 */
export function getOrCreateDiscussion(discussions, flowName, context, useContextAsId, enableHitl) {
    // search for an existing discussion first.
    const foundDiscussion = findDiscussion(discussions, flowName, context, useContextAsId);
    if (foundDiscussion) {
        return foundDiscussion;
    }
    // not found, create the new discussion.
    const newDiscussion = createNewDiscussion(flowName, context, useContextAsId, enableHitl);
    // mutate the set of discussions.
    discussions.push(newDiscussion);
    // returns the created discussion.
    return newDiscussion;
}
/**
 * Create a new chat session.
 *
 * @param discussion the discussion.
 * @returns a new discussion object.
 */
export function createNewChatSession(discussion) {
    // create new session.
    const newChatSession = {
        messages: [],
        isOpen: true,
    };
    // push in the discussion.
    discussion.chatSessions.push(newChatSession);
    // return the newly created chat session.
    return newChatSession;
}
/**
 * Close the chat session.
 */
export function closeChatSession(discussion, sessionId) {
    const foundChatSession = discussion.chatSessions.find(chatSession => chatSession.sessionId === sessionId);
    // guard.
    if (!foundChatSession) {
        throw new Error('Illegal state: no chat session found.');
    }
    // guard.
    if (!foundChatSession.isOpen) {
        throw new Error('Illegal state: unable to close an already closed chat session.');
    }
    // reset values.
    foundChatSession.isOpen = false;
    discussion.isWaitingForResponse = false;
    discussion.isWaitingForHitlUserAnswer = false;
}
/**
 * Get the open chat session.
 *
 * @param discussion the discussion.
 * @returns the open chat session.
 */
export function getOpenChatSession(discussion) {
    // guard.
    if (discussion.chatSessions.length === 0) {
        throw new Error('Illegal state: the discussion contains no chat session.');
    }
    // find the open chat session.
    const foundChatSession = discussion.chatSessions.find(session => session.isOpen);
    // guard.
    if (!foundChatSession) {
        throw new Error('Illegal state: the discussion contains no open chat session.');
    }
    return foundChatSession;
}
/**
 * The chat session has a welcome message.
 * @param chatSession the chatSession.
 * @returns true if the message session has already a welcome message.
 */
export function hasWelcomeMessage(chatSession) {
    return chatSession.messages.some(message => message.kind === ChatMessageKind.WELCOME_MESSAGE);
}
/**
 * Add a message to the given chat session.
 */
export function addMessageToChatSession(kind, chatSession, parts) {
    // trivial case: the session is closed.
    if (!chatSession.isOpen) {
        return;
    }
    // add the message.
    switch (kind) {
        case ChatMessageKind.AI:
            // store the query id to identify messages (ex. thumbs up need to send the query id).
            if (!chatSession.queryId) {
                throw new Error('Illegal state: query id is not set.');
            }
            chatSession.messages.push({
                kind,
                parts,
                queryId: chatSession.queryId,
            });
            break;
        case ChatMessageKind.HUMAN:
            chatSession.messages.push({
                kind,
                parts,
            });
            break;
        case ChatMessageKind.WELCOME_MESSAGE:
            chatSession.messages.push({
                kind,
                parts,
            });
            break;
        case ChatMessageKind.SUGGESTED_PROMPTS:
            chatSession.messages.push({
                kind,
                parts,
            });
            break;
        default:
            unreacheable(kind);
    }
}
/**
 * Find a message.
 */
export function findAiMessage(discussion, queryId) {
    for (const chatSession of discussion.chatSessions) {
        // iterate over chat sessions.
        for (const message of chatSession.messages) {
            // iterate over messages.
            if (message.kind === ChatMessageKind.AI) {
                if (message.queryId === queryId) {
                    return message;
                }
            }
        }
    }
    return undefined;
}
/**
 * Convert a simple text to a multipart message.
 */
export function toMarkdownMultiPartMessage(text) {
    return [
        {
            kind: MessagePartKind.MARKDOWN,
            data: text,
        },
    ];
}
/**
 * Convert a text to a multipart message.
 */
export function toTextMultiPartMessage(text) {
    return [...text].map(data => ({
        kind: MessagePartKind.TEXT,
        data,
    }));
}
/**
 * Clone the discussion settings.
 */
export function cloneDiscussionSettings(settings) {
    return clone(settings);
}
//# sourceMappingURL=discussion-utils.js.map